import { AxiosError } from 'axios';

import { api } from '@/lib/api/axios';
import { ApiError, toApiError } from '@/lib/api/error';
import type { PaginatedResult } from '@/types/roles';
import type {
  CreateSiteBody,
  DeactivateBlocked,
  SiteDetail,
  SiteListItem,
  SiteUserItem,
  SitesListQuery,
  UpdateSiteBody,
} from '@/types/sites';

/**
 * Typed wrappers around the 7 `/api/sites` endpoints from Plan 09-01.
 *
 * Every wrapper:
 *  - Throws an `ApiError` (via `toApiError`) on non-2xx so consumers can
 *    `instanceof`-switch on `e.status`.
 *  - Returns the typed response object on success.
 *
 * Permission gating is UI-only convenience (T-09-12). The backend re-checks
 * every permission via `[RequirePermission(...)]` (Plan 09-01).
 */

function buildListUrl(q: SitesListQuery): string {
  const params = new URLSearchParams();
  if (q.q !== undefined && q.q !== '') params.set('q', q.q);
  if (q.status !== undefined) params.set('status', q.status);
  params.set('page', String(q.page ?? 1));
  params.set('pageSize', String(q.pageSize ?? 20));
  return `/api/sites?${params.toString()}`;
}

export async function listSitesRequest(
  query: SitesListQuery = {},
): Promise<PaginatedResult<SiteListItem>> {
  try {
    const { data } = await api.get<PaginatedResult<SiteListItem>>(buildListUrl(query));
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

export async function getSiteRequest(id: number): Promise<SiteDetail> {
  try {
    const { data } = await api.get<SiteDetail>(`/api/sites/${String(id)}`);
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

export interface SiteUsersQuery {
  readonly status?: 'active' | 'inactive' | 'all';
  readonly page?: number;
  readonly pageSize?: number;
}

export async function getSiteUsersRequest(
  id: number,
  query: SiteUsersQuery = {},
): Promise<PaginatedResult<SiteUserItem>> {
  try {
    const params = new URLSearchParams();
    if (query.status !== undefined) params.set('status', query.status);
    params.set('page', String(query.page ?? 1));
    params.set('pageSize', String(query.pageSize ?? 20));
    const { data } = await api.get<PaginatedResult<SiteUserItem>>(
      `/api/sites/${String(id)}/users?${params.toString()}`,
    );
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

export async function createSiteRequest(body: CreateSiteBody): Promise<SiteDetail> {
  try {
    const { data } = await api.post<SiteDetail>('/api/sites', body);
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

export async function updateSiteRequest(id: number, body: UpdateSiteBody): Promise<SiteDetail> {
  try {
    const { data } = await api.put<SiteDetail>(`/api/sites/${String(id)}`, body);
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

/** Specialized ApiError thrown by `deactivateSiteRequest` when the backend
 * returns 400 with `requiresOverride: true` plus the live counts. The caller
 * (DeactivateSiteConfirmDialog) reads `.blocked` to render the override UX. */
export class DeactivateSiteBlockedError extends ApiError {
  public readonly blocked: DeactivateBlocked;

  constructor(message: string, status: number, blocked: DeactivateBlocked, traceId?: string) {
    const opts: { traceId?: string } = traceId !== undefined ? { traceId } : {};
    super(message, status, opts);
    this.name = 'DeactivateSiteBlockedError';
    this.blocked = blocked;
  }
}

/** Type-guard for the override-required error path. */
export function isDeactivateBlocked(e: unknown): e is DeactivateSiteBlockedError {
  return e instanceof DeactivateSiteBlockedError;
}

/**
 * Soft-deactivate a site.
 *
 * `force=true` bypasses the active-machines / pending-logsheets blocker
 * check on the backend. When force is `false` (the default) and the site
 * has live machines or pending logsheets, the backend returns 400 with
 * `extensions.requiresOverride === true` plus the actual counts. The wrapper
 * inspects the response body and rethrows a `DeactivateSiteBlockedError`
 * carrying the structured counts — callers use `isDeactivateBlocked(e)` to
 * decide whether to surface the override checkbox.
 */
export async function deactivateSiteRequest(id: number, force = false): Promise<void> {
  try {
    const url = force ? `/api/sites/${String(id)}?force=true` : `/api/sites/${String(id)}`;
    await api.delete(url);
  } catch (e) {
    if (e instanceof AxiosError && e.response?.status === 400) {
      const body = e.response.data as
        | {
            requiresOverride?: boolean;
            activeMachinesCount?: number;
            pendingLogsheetsCount?: number;
            title?: string;
            detail?: string;
            traceId?: string;
          }
        | undefined;
      if (
        body?.requiresOverride === true &&
        typeof body.activeMachinesCount === 'number' &&
        typeof body.pendingLogsheetsCount === 'number'
      ) {
        throw new DeactivateSiteBlockedError(
          body.title ?? 'Validation Failed',
          400,
          {
            activeMachinesCount: body.activeMachinesCount,
            pendingLogsheetsCount: body.pendingLogsheetsCount,
            requiresOverride: true,
          },
          body.traceId,
        );
      }
    }
    throw toApiError(e);
  }
}

export async function reactivateSiteRequest(id: number): Promise<void> {
  try {
    await api.post(`/api/sites/${String(id)}/reactivate`);
  } catch (e) {
    throw toApiError(e);
  }
}
